import { morphSection } from '@theme/section-renderer';
import { StandardEvents } from '@shopify/events';

const CHOICE_KEY = 'Correa incluida';
const FOR_KEY = 'Para el reloj';
const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

/* ---------- UTM del colaborador: se guarda en el carrito para que llegue al pedido ---------- */
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
const UTM_STORE = 'dgl_utm';
const UTM_TTL = 30 * 24 * 60 * 60 * 1000;

function readStoredUtm() {
  try {
    const raw = localStorage.getItem(UTM_STORE);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data || Date.now() - data.t > UTM_TTL) return null;
    return data.v;
  } catch {
    return null;
  }
}

function captureUtm() {
  const params = new URLSearchParams(window.location.search);
  const found = {};
  for (const key of UTM_KEYS) {
    const value = params.get(key);
    if (value) found[key] = value.slice(0, 100);
  }
  if (!found.utm_source) return;
  try {
    localStorage.setItem(UTM_STORE, JSON.stringify({ t: Date.now(), v: found }));
  } catch {
    /* sin almacenamiento: se intentará con el carrito en esta misma página */
  }
}

let utmSyncing = false;
async function syncUtm() {
  if (utmSyncing) return;
  const stored = readStoredUtm();
  if (!stored) return;
  utmSyncing = true;
  try {
    const cart = await (await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })).json();
    if (!cart.item_count) return;
    const attributes = {};
    for (const key of UTM_KEYS) {
      if (stored[key] && cart.attributes?.[key] !== stored[key]) attributes[key] = stored[key];
    }
    if (!Object.keys(attributes).length) return;
    await fetch(`${root}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes }),
    });
  } catch (error) {
    console.error(error);
  } finally {
    utmSyncing = false;
  }
}

captureUtm();
syncUtm();
document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  event.promise?.then(() => syncUtm()).catch(() => {});
});

/* ---------- Carrito: correa por reloj ---------- */
const cartSections = () =>
  [...document.querySelectorAll('cart-items-component')]
    .map((el) => ({ id: el.dataset.sectionId, drawer: el.dataset.drawer !== undefined }))
    .filter((s) => s.id);

const watches = () => [...document.querySelectorAll('[data-dgl-watch]')];
const missing = () => watches().filter((w) => !w.dataset.chosen);
const blocked = () => missing().length > 0;

// Mientras falte la correa de algún reloj: botón de pagar atenuado y pagos rápidos ocultos.
function gate() {
  const needs = blocked();
  document.querySelectorAll('button[name="checkout"]').forEach((btn) => {
    btn.classList.toggle('dgl-blocked', needs);
  });
  document.querySelectorAll('.additional-checkout-buttons').forEach((el) => {
    el.style.display = needs ? 'none' : '';
  });
}

function warn() {
  const pending = missing();
  pending.forEach((w) => w.classList.add('dgl-watch--error'));
  if (pending[0]) pending[0].scrollIntoView({ block: 'center', behavior: 'smooth' });
}

async function post(path, payload) {
  const secs = cartSections();
  const body = { ...payload };
  if (secs.length) {
    body.sections = secs.map((s) => s.id).join(',');
    body.sections_url = window.location.pathname;
  }
  const res = await fetch(`${root}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await res.json();
  if (data.status && Number(data.status) >= 400) throw new Error(data.description || data.message || 'Error');
  if (data.sections) {
    for (const s of secs) {
      if (data.sections[s.id]) {
        await morphSection(s.id, data.sections[s.id], { mode: s.drawer ? 'hydration' : 'full' });
      }
    }
  }
  gate();
  syncUtm();
  return data;
}

// Cola: los cambios se envían de uno en uno para no pisarse entre relojes.
let queue = Promise.resolve();
const enqueue = (job) => {
  queue = queue.then(job).catch((error) => console.error(error));
  return queue;
};

document.addEventListener('change', (event) => {
  const input = event.target;
  // Solo las elecciones hechas por el cliente: ignora cambios generados al repintar el carrito.
  if (!event.isTrusted) return;
  if (!(input instanceof HTMLInputElement) || !input.matches('[data-dgl-choice]')) return;
  const watch = input.closest('[data-dgl-watch]');
  if (!watch) return;
  watch.dataset.chosen = input.value;
  watch.classList.remove('dgl-watch--error');
  gate();
  const key = watch.dataset.dglKey;
  const quantity = Number(watch.dataset.dglQty) || 1;
  const value = input.value;
  enqueue(() => post('cart/change.js', { id: key, quantity, properties: { [CHOICE_KEY]: value } }));
});

document.addEventListener(
  'click',
  (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    // Botón "Ver carrito" de la ficha: abre el carrito lateral (si no existe, va a la página del carrito).
    const openCart = target.closest('[data-dgl-open-cart]');
    if (openCart) {
      const drawer = document.querySelector('theme-drawer#cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        event.preventDefault();
        drawer.open();
      }
      return;
    }

    const extra = target.closest('[data-dgl-extra]');
    if (extra instanceof HTMLButtonElement) {
      event.preventDefault();
      extra.disabled = true;
      const id = Number(extra.dataset.dglExtra);
      const owner = extra.dataset.dglFor;
      const lineKey = extra.dataset.dglExtraKey;
      const inCart = extra.dataset.inCart === 'true';
      enqueue(async () => {
        try {
          if (inCart && lineKey) {
            await post('cart/change.js', { id: lineKey, quantity: 0 });
          } else {
            await post('cart/add.js', { items: [{ id, quantity: 1, properties: { [FOR_KEY]: owner } }] });
          }
        } catch (error) {
          extra.disabled = false;
          throw error;
        }
      });
      return;
    }

    const checkout = target.closest('button[name="checkout"]');
    if (checkout && blocked()) {
      event.preventDefault();
      event.stopImmediatePropagation();
      warn();
    }
  },
  true
);

// Quita las correas extra que se quedaron sin reloj (por ejemplo si se elimina el reloj del carrito).
const removing = new Set();
function cleanOrphans() {
  document.querySelectorAll('[data-dgl-orphan]').forEach((el) => {
    const key = el.dataset.dglOrphan;
    if (!key || removing.has(key)) return;
    removing.add(key);
    enqueue(() => post('cart/change.js', { id: key, quantity: 0 }));
  });
}

new MutationObserver(() => {
  gate();
  cleanOrphans();
}).observe(document.body, { childList: true, subtree: true });
gate();
cleanOrphans();
